import { type ReactNode, useSyncExternalStore } from 'react';
import { createPortal } from 'react-dom';

interface PortalProps {
	children: ReactNode;
}

// Nothing to subscribe to: the only change is server/hydration -> client.
const subscribe = () => () => {};
const useIsHydrated = () =>
	useSyncExternalStore(
		subscribe,
		() => true,
		() => false,
	);

/**
 * Renders its children into `#portal-container` (see `_document.tsx`), outside
 * the page's stacking context, so overlays are never clipped by a parent.
 * Renders nothing on the server and during hydration, when there is no
 * document to portal into yet.
 */
export const Portal = ({ children }: PortalProps) => {
	const isHydrated = useIsHydrated();
	if (!isHydrated) return null;

	return createPortal(
		children,
		document.querySelector('#portal-container') ?? document.body,
	);
};
